import { useEffect, useRef, useState } from 'react';

import { cn } from '@/utils/tailwind';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type MarketStoreListItem } from '../../_consts/constants';
import { getMarketKey } from '../../_utils/getMarketKey';

import ReviewMarketTabItem from './ReviewMarketTabItem';
import ReviewTabIndicator from './ReviewTabIndicator';

interface MarketTabSectionProps {
  marketList: MarketStoreListItem[];
  selectedMarket: MarketStoreListItem | null;
  setSelectedMarket: (market: MarketStoreListItem) => void;
  marketOrderCounts: { key: string; orderCount: number }[];
}

const SCROLL_AMOUNT = 300;

const MarketTabSectionReview = ({
  marketList,
  selectedMarket,
  setSelectedMarket,
  marketOrderCounts,
}: MarketTabSectionProps) => {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const tabBarRef = useRef<HTMLDivElement | null>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  // 스크롤 상태 체크
  useEffect(() => {
    const checkScroll = () => {
      const el = tabBarRef.current;
      if (!el) return;
      setShowLeft(el.scrollLeft > 0);
      setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    checkScroll();
    const el = tabBarRef.current;
    el?.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el?.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [marketList.length]);

  const scrollLeft = () => {
    tabBarRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
  };
  const scrollRight = () => {
    tabBarRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
  };
  return (
    <div className="flex min-h-[46px] w-full justify-between gap-[30px]">
      <div className=" relative flex min-h-[46px]  w-fit max-w-full flex-col">
        {showLeft && (
          <button
            className="absolute -left-5 top-1/2 z-20 -translate-y-1/2 rounded-full bg-white  px-1 py-1 text-gr-700 shadow-md"
            onClick={scrollLeft}
            aria-label="왼쪽으로 스크롤"
          >
            <ChevronLeft />
          </button>
        )}

        <div className="grid ">
          <div
            className={cn(
              'relative flex  min-h-[46px] flex-nowrap gap-x-[6px] overflow-x-auto  rounded-[12px] bg-gr-100 p-1   scrollbar-hide',
              'max-w-full',
            )}
            ref={tabBarRef}
          >
            <ReviewTabIndicator marketList={marketList} tabRefs={tabRefs} tabBarRef={tabBarRef} />
            {marketList.map((market) => {
              const uniqueKey = getMarketKey({
                marketId: market.MarketId,
                storeId: market.StoreId,
              });
              const isActive =
                selectedMarket &&
                getMarketKey({
                  marketId: selectedMarket.MarketId,
                  storeId: selectedMarket.StoreId,
                }) === uniqueKey;
              const isHovered = hoveredKey === uniqueKey;
              const orderCount =
                marketOrderCounts.find((mc) => mc.key === uniqueKey)?.orderCount ?? 0;

              return (
                <ReviewMarketTabItem
                  uniqueKey={uniqueKey}
                  tabRefs={tabRefs}
                  setHoveredKey={setHoveredKey}
                  setSelectedMarket={setSelectedMarket}
                  market={market}
                  isActive={isActive}
                  isHovered={isHovered}
                  key={uniqueKey}
                  hasOrder={orderCount > 0}
                />
              );
            })}
          </div>
          {showRight && (
            <button
              className="absolute -right-5 top-1/2 z-20 -translate-y-1/2 rounded-full bg-white px-1 py-1 text-gr-700 shadow-md"
              onClick={scrollRight}
              aria-label="오른쪽으로 스크롤"
            >
              <ChevronRight />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketTabSectionReview;
